import { useState, useEffect } from 'react';
import { useNavigate, Link, Outlet, useLocation } from 'react-router-dom';
import { adminApi } from '../../lib/api';
import { 
  MessageSquare, 
  FolderKanban, 
  LogOut, 
  Home,
  Menu,
  X
} from 'lucide-react';

export const AdminLayout = () => {
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('pera_admin_token');
      if (!token) {
        navigate('/admin/login');
        return;
      }

      try {
        const response = await adminApi.getMe();
        setAdmin(response.data);
      } catch (error) {
        localStorage.removeItem('pera_admin_token');
        navigate('/admin/login');
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('pera_admin_token');
    navigate('/admin/login');
  };

  const navItems = [
    { href: '/admin', label: 'İletişim Formları', icon: MessageSquare },
    { href: '/admin/projects', label: 'Projeler', icon: FolderKanban },
  ];

  const isActive = (href) => {
    if (href === '/admin') return location.pathname === '/admin';
    return location.pathname.startsWith(href);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f2f2f7] flex items-center justify-center">
        <div className="animate-pulse text-[#8e8e93]">Yükleniyor...</div>
      </div>
    );
  }

  return (
    <div data-testid="admin-layout" className="min-h-screen bg-[#f2f2f7] flex">
      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-[#1a1a1a] transform transition-transform lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="p-6 border-b border-[#8e8e93]/30">
          <Link to="/" className="flex items-center gap-3">
            <img
              src="/logo-vepera.png"
              alt="VE PERA"
              className="h-8 w-auto brightness-0 invert"
            />
            <span className="text-xs text-[#b87333] uppercase tracking-wider">Admin</span>
          </Link>
        </div>

        <nav className="p-4">
          <ul className="space-y-1">
            {navItems.map((item) => (
              <li key={item.href}>
                <Link
                  to={item.href}
                  data-testid={`admin-nav-${item.href.split('/').pop() || 'contacts'}`}
                  onClick={() => setSidebarOpen(false)}
                  className={`admin-nav-item ${isActive(item.href) ? 'active' : ''}`}
                >
                  <item.icon size={20} />
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-[#8e8e93]/30">
          <Link
            to="/"
            className="admin-nav-item mb-2"
          >
            <Home size={20} />
            Siteye Don
          </Link>
          <button
            onClick={handleLogout}
            data-testid="admin-logout"
            className="admin-nav-item w-full text-left"
          >
            <LogOut size={20} />
            Çıkış Yap
          </button>
        </div>
      </aside>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        ></div>
      )}

      {/* Main Content */}
      <div className="flex-1 lg:ml-64">
        {/* Top Bar */}
        <header className="bg-white border-b border-[#d1d1d6] px-4 lg:px-8 py-4 flex items-center justify-between">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="lg:hidden p-2 text-[#1a1a1a]"
            data-testid="admin-menu-toggle"
          >
            {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
          </button>

          <div className="flex items-center gap-4 ml-auto">
            <span className="text-sm text-[#8e8e93]">{admin?.email}</span>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-4 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
